import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { AuthConfig, AuthConfigUser } from '@wfp-common/models';

import { authLogin } from '../../apiClient';

interface AuthContextType extends Pick<AuthConfig, 'isPathOnProtectedLocation' | 'appHomePath' | 'appEntryPath'> {
    loggedUser: AuthConfigUser | null;
    isUserLogged: boolean;
}

const AuthContext = createContext<AuthContextType>(null);

interface AuthContextProviderProps {
    authConfig: () => AuthConfig;
}

export const AuthContextProvider = (props: PropsWithChildren<AuthContextProviderProps>) => {
    const authConfig = props.authConfig();
    const [loginTriggered, setLoginTriggered] = React.useState(false);

    useEffect(() => {
        authConfig.initApp();
    }, []);

    const value: AuthContextType = useMemo(
        () => ({
            loggedUser: authConfig.getUser(),
            isUserLogged: authConfig.isUserLogged && authConfig.isAppReadyToUse,
            appEntryPath: authConfig.appEntryPath,
            appHomePath: authConfig.appHomePath,
            isPathOnProtectedLocation: authConfig.isPathOnProtectedLocation,
        }),
        [authConfig]
    );

    if (!authConfig.getApiToken() && authConfig.isPathOnProtectedLocation && !loginTriggered) {
        const currentPath = window.location.pathname + window.location.search;
        sessionStorage.setItem('returnUrl', currentPath);
        setLoginTriggered(true);

        authLogin({ returnUrl: currentPath })
            .then((response) => {
                if (response?.login_url) {
                    window.location.replace(response.login_url);
                }
            })
            .catch(() => {
                window.location.replace(authConfig.appEntryPath);
            });

        return null;
    }

    return <AuthContext.Provider value={value} {...props} />;
};

export const useAuthContext = () => {
    const context = useContext<AuthContextType>(AuthContext);

    if (!context) {
        throw new Error('You need to wrap AuthContextProvider');
    }

    return context;
};
